import {
  html,
  attr,
  prop,
  Signal,
  When,
  Value,
  Fragment,
  on,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Button, Icon, Modal, TextInput, InputWrapper } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { LoadingState, ErrorState, StatusBadge, toast } from '@/components/ui'
import { projects as projectsService, authStore } from '@/services/gateway'
import {
  formatDate,
  capitalize,
  mapRoleToVariant,
  mapMemberStatusToVariant,
} from '@/utils'

// ============================================================================
// Types
// ============================================================================

interface Project {
  id: string
  orgId: string
  name: string
}

interface Member {
  id: string
  userId: string
  email: string
  displayName: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  status: 'active' | 'pending' | 'revoked'
  joinedAt: string
}

// ============================================================================
// Project Members Page Component
// ============================================================================

export interface ProjectMembersPageOptions {
  projectId: Value<string>
  memberId?: Value<string>
}

export function ProjectMembersPage({
  projectId,
  memberId,
}: ProjectMembersPageOptions) {
  const project = prop<Project | null>(null)
  const members = prop<Member[]>([])
  const isLoading = prop(true)
  const error = prop<string | null>(null)
  const isOwnerOrAdmin = prop(false)

  // Invite modal state
  const inviteEmail = prop('')
  const inviteRole = prop<'admin' | 'member' | 'viewer'>('member')
  const inviting = prop(false)
  const inviteError = prop<string | null>(null)

  // Load data
  loadProject()

  async function loadProject() {
    isLoading.set(true)
    error.set(null)

    try {
      const projId = typeof projectId === 'string' ? projectId : projectId.get()
      const projectData = await projectsService.getProject(projId)

      // Get members
      const membersList: Member[] = []
      try {
        const membersData = await projectsService.listProjectMembers(projId)

        // Map members directly - API now returns email and displayName
        for (let idx = 0; idx < membersData.members.length; idx++) {
          const m = membersData.members[idx]
          membersList.push({
            id: m.id || `member_${idx}`,
            userId: m.userId,
            email: m.email || m.userId,
            displayName: m.displayName || m.email || m.userId,
            role: m.role as 'owner' | 'admin' | 'member' | 'viewer',
            status: m.status as 'active' | 'pending' | 'revoked',
            joinedAt: m.joinedAt || '',
          })
        }
      } catch {
        // Ignore member fetch errors
      }

      project.set({
        id: projectData.id,
        orgId: projectData.orgId,
        name: projectData.name,
      })
      members.set(membersList)

      // Check current user's role
      const currentUserId = authStore.user.get()?.id
      const currentMember = membersList.find((m) => m.userId === currentUserId)
      isOwnerOrAdmin.set(
        currentMember?.role === 'owner' || currentMember?.role === 'admin'
      )
    } catch (err) {
      error.set(err instanceof Error ? err.message : 'Failed to load project')
    } finally {
      isLoading.set(false)
    }
  }

  const projId = typeof projectId === 'string' ? projectId : projectId.get()

  // Handle invite member
  async function handleInviteMember(closeModal: () => void) {
    const email = inviteEmail.get().trim()
    const role = inviteRole.get()

    if (!email) {
      inviteError.set('Email is required')
      return
    }

    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      inviteError.set('Please enter a valid email address')
      return
    }

    try {
      inviting.set(true)
      inviteError.set(null)
      await projectsService.inviteProjectMember(projId, email, role)
      toast.success(`Invitation sent to ${email}`)
      inviteEmail.set('')
      inviteRole.set('member')
      closeModal()
      // Reload members list
      loadProject()
    } catch (err) {
      inviteError.set(
        err instanceof Error ? err.message : 'Failed to send invitation'
      )
    } finally {
      inviting.set(false)
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        { href: `/p/${projId}` },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      html.span(
        project.map((p) => p?.name || 'Project'),
        html.span(attr.class('text-gray-400'), ' / Members')
      )
    ),
    content: Fragment(
      // Loading state
      When(isLoading, () =>
        LoadingState({ message: 'Loading members...', size: 'lg' })
      ),
      // Error state
      When(
        error.map((e) => e !== null),
        () => ErrorState({ error })
      ),
      // Main content
      When(
        project.map((p) => p !== null),
        () =>
          html.div(
            attr.class('space-y-6'),
            // Header with invite button
            html.div(
              attr.class('flex items-center justify-between'),
              html.p(
                attr.class('text-sm text-gray-600'),
                'Manage project members and permissions'
              ),
              When(isOwnerOrAdmin, () =>
                Modal(
                  { size: 'md', dismissable: true },
                  (openModal, closeModal) =>
                    html.div(
                      Button(
                        {
                          onClick: () => {
                            inviteEmail.set('')
                            inviteRole.set('member')
                            inviteError.set(null)
                            openModal({
                              header: html.h2(
                                attr.class(
                                  'text-lg font-semibold text-gray-900'
                                ),
                                'Invite Team Member'
                              ),
                              body: html.div(
                                attr.class('space-y-4'),
                                // Error message
                                When(
                                  inviteError.map((e) => e !== null),
                                  () =>
                                    html.div(
                                      attr.class(
                                        'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
                                      ),
                                      inviteError
                                    )
                                ),
                                // Email input
                                InputWrapper({
                                  label: 'Email Address',
                                  content: TextInput({
                                    value: inviteEmail,
                                    onInput: (v) => inviteEmail.set(v),
                                    placeholder: 'member@example.com',
                                  }),
                                }),
                                // Role selector
                                InputWrapper({
                                  label: 'Role',
                                  description:
                                    'Select the permission level for this member',
                                  content: html.select(
                                    attr.class(
                                      'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                                    ),
                                    attr.value(inviteRole),
                                    on.change((e) =>
                                      inviteRole.set(
                                        (e.target as HTMLSelectElement)
                                          .value as
                                          | 'admin'
                                          | 'member'
                                          | 'viewer'
                                      )
                                    ),
                                    html.option(
                                      attr.value('viewer'),
                                      'Viewer - Read-only access'
                                    ),
                                    html.option(
                                      attr.value('member'),
                                      'Member - Can manage files and actions'
                                    ),
                                    html.option(
                                      attr.value('admin'),
                                      'Admin - Full project access'
                                    )
                                  ),
                                })
                              ),
                              footer: html.div(
                                attr.class('flex justify-end gap-3'),
                                Button(
                                  {
                                    variant: 'outline',
                                    onClick: closeModal,
                                    disabled: inviting,
                                  },
                                  'Cancel'
                                ),
                                Button(
                                  {
                                    onClick: () =>
                                      handleInviteMember(closeModal),
                                    disabled: inviting,
                                  },
                                  inviting.map((i) =>
                                    i ? 'Sending...' : 'Send Invitation'
                                  )
                                )
                              ),
                            })
                          },
                        },
                        Icon(
                          { icon: 'mdi:account-plus' },
                          attr.class('w-4 h-4 mr-2')
                        ),
                        'Invite Member'
                      )
                    )
                )
              )
            ),
            // Members table
            html.div(
              attr.class(
                'bg-white rounded-xl border border-gray-200 overflow-hidden'
              ),
              DataTable<Member>({
                columns: [
                  {
                    key: 'email',
                    header: 'Member',
                    render: (item) => {
                      const isHighlighted = memberId
                        ? item.map((m) => {
                            const targetId =
                              typeof memberId === 'string'
                                ? memberId
                                : memberId.get()
                            return m.userId === targetId
                          })
                        : prop(false)

                      return html.div(
                        attr.class(
                          isHighlighted.map((highlighted) =>
                            highlighted
                              ? 'pl-3 border-l-4 border-brand-600'
                              : ''
                          )
                        ),
                        html.p(
                          attr.class('font-medium text-gray-900'),
                          item.map((m) => m.displayName)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          item.map((m) => m.email)
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          'Joined ',
                          item.map((m) =>
                            m.joinedAt ? formatDate(m.joinedAt) : 'N/A'
                          )
                        )
                      )
                    },
                  },
                  {
                    key: 'role',
                    header: 'Role',
                    render: (item) =>
                      StatusBadge({
                        label: item.map((m) => capitalize(m.role)),
                        variant: item.map((m) => mapRoleToVariant(m.role)),
                      }),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (item) =>
                      StatusBadge({
                        label: item.map((m) => capitalize(m.status)),
                        variant: item.map((m) =>
                          mapMemberStatusToVariant(m.status)
                        ),
                      }),
                  },
                  {
                    key: 'id',
                    header: '',
                    render: (item) =>
                      When(isOwnerOrAdmin, () =>
                        Anchor(
                          {
                            href: item.map(
                              (m) => `/p/${projId}/members/${m.userId}`
                            ),
                          },
                          attr.class(
                            'text-brand-600 hover:text-brand-700 text-sm font-medium'
                          ),
                          'Manage'
                        )
                      ),
                  },
                ],
                data: members,
                renderMobileCard: (member) =>
                  MemberCard(member, isOwnerOrAdmin, projId, memberId),
                emptyMessage: 'No members yet.',
              })
            )
          )
      )
    ),
  })
}

// ============================================================================
// Mobile Card Component
// ============================================================================

function MemberCard(
  member: Signal<Member>,
  isOwnerOrAdmin: Signal<boolean>,
  projectId: string,
  memberId?: Value<string>
) {
  // Check if this member is the highlighted one
  const isHighlighted = memberId
    ? member.map((m) => {
        const targetId =
          typeof memberId === 'string' ? memberId : memberId.get()
        return m.userId === targetId
      })
    : prop(false)

  return html.div(
    attr.class(
      isHighlighted.map((highlighted) =>
        highlighted
          ? 'bg-brand-50 border-l-4 border-brand-600 border border-gray-200 rounded-lg p-4'
          : 'bg-white border border-gray-200 rounded-lg p-4'
      )
    ),
    html.div(
      attr.class('flex items-start justify-between mb-3'),
      html.div(
        html.h3(
          attr.class('font-medium text-gray-900'),
          member.map((m) => m.displayName)
        ),
        html.p(
          attr.class('text-sm text-gray-500'),
          member.map((m) => m.email)
        ),
        html.p(
          attr.class('text-sm text-gray-500 mt-1'),
          'Joined ',
          member.map((m) => (m.joinedAt ? formatDate(m.joinedAt) : 'N/A'))
        )
      ),
      When(isOwnerOrAdmin, () =>
        Anchor(
          { href: member.map((m) => `/p/${projectId}/members/${m.userId}`) },
          attr.class('text-brand-600 hover:text-brand-700'),
          Icon({ icon: 'mdi:dots-vertical' }, attr.class('w-5 h-5'))
        )
      )
    ),
    html.div(
      attr.class('flex items-center gap-2'),
      StatusBadge({
        label: member.map((m) => capitalize(m.role)),
        variant: member.map((m) => mapRoleToVariant(m.role)),
      }),
      StatusBadge({
        label: member.map((m) => capitalize(m.status)),
        variant: member.map((m) => mapMemberStatusToVariant(m.status)),
      })
    )
  )
}
